import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { colors, shadows } from '../../employees/styles/personalakteStyles';
import OffersListTab from './OffersListTab';
import ProjectsListTab from './ProjectsListTab';
import InvoicesListTab from './InvoicesListTab';
import CatalogTab from './CatalogTab';
import SalesDashboardBoard from './SalesDashboardBoard';
import CrmTaskModal from './CrmTaskModal';

type SalesTabKey = 'dashboard' | 'offers' | 'projects' | 'invoices' | 'catalog';

const TABS: { key: SalesTabKey; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'offers', label: 'Offers' },
  { key: 'projects', label: 'Projects' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'catalog', label: 'Catalog' },
];

export default function SalesPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTaskModal, setShowTaskModal] = useState(false);

  const urlTab = searchParams.get('tab') as SalesTabKey;
  const activeTab: SalesTabKey = TABS.some((t) => t.key === urlTab) ? urlTab : 'dashboard';

  // Open task modal via query param (from QuickAddButton)
  useEffect(() => {
    if (searchParams.get('addTask') === 'true') {
      setShowTaskModal(true);
    }
  }, [searchParams]);

  const handleTabChange = (tab: SalesTabKey) => {
    // Only keep the tab param when switching tabs, remove other filters
    setSearchParams({ tab });
  };

  const handleTaskModalClose = () => {
    setShowTaskModal(false);
    if (searchParams.get('addTask')) {
      searchParams.delete('addTask');
      setSearchParams(searchParams);
    }
  };

  const handleTaskModalSaved = () => {
    setShowTaskModal(false);
    if (searchParams.get('addTask')) {
      searchParams.delete('addTask');
      setSearchParams(searchParams);
    }
  };

  return (
    <div style={{ padding: '16px', position: 'relative', fontSize: '0.9rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <div
        className="glass-header"
        style={{ padding: '12px 16px', marginBottom: '16px', position: 'relative', zIndex: 100 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          {/* Tab Navigation */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    border: isActive ? '1px solid rgba(82, 113, 165, 0.2)' : '1px solid transparent',
                    background: isActive ? 'rgba(82, 113, 165, 0.12)' : 'transparent',
                    color: isActive ? colors.slate : colors.textMuted,
                    borderRadius: 9999,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(82, 113, 165, 0.06)';
                      e.currentTarget.style.color = colors.slate;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = colors.textMuted;
                    }
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && <SalesDashboardBoard />}

      {activeTab !== 'dashboard' && (
        <div
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
            minHeight: '400px',
          }}
        >
          {activeTab === 'offers' && <OffersListTab />}
          {activeTab === 'projects' && <ProjectsListTab />}
          {activeTab === 'invoices' && <InvoicesListTab />}
          {activeTab === 'catalog' && <CatalogTab />}
        </div>
      )}

      {/* Task Modal (opened via QuickAddButton) */}
      <CrmTaskModal
        isOpen={showTaskModal}
        onClose={handleTaskModalClose}
        onSaved={handleTaskModalSaved}
      />
    </div>
  );
}
